import { Component, OnDestroy, OnInit, inject, ElementRef, ViewChild } from '@angular/core';
import { AppViewportScroller } from '../../../core/services';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';

import { AdminService } from '../services/admin.service';
import { AdminFilterField, countActiveFilters, openAdminFilters } from '../filters-sheet/admin-filters-sheet';
import { ModalService } from '../../../shared/modal';
import { isPhoneViewport } from '../../../core/utils/phone';
import { AdminCallRow, AdminCallTotals } from '../models/admin-call.model';
import { AdminPagination } from '../models/admin-pagination.model';
import { LatencyPipe } from '../../../core/pipes';
import { dateRangeLabel, filterLabel } from '../utils/filter-label';
import { SourceSlice, sourceSlices, sourceSlicesLabel } from '../utils/source-split';

const PER_PAGE = 50;

/** Filters live in the URL (?q=&endpoint=&source=&api_key_id=&errors_only=&
 *  from=&to=&page=) per the contract's "Screens" note, and are how
 *  Organization Detail's cross-links ("All calls", a key row, "View all
 *  calls") actually land here pre-filtered. `q` filters by organization
 *  (slug, id, or name) confirmed against the real API, not the written
 *  contract, which named this param `organization` instead. */
@Component({
  selector: 'app-admin-calls',
  standalone: false,
  templateUrl: './admin-calls.html',
  styleUrl: './admin-calls.scss',
})
export class AdminCalls implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();

  rows: AdminCallRow[] = [];
  /** For the whole filtered list (every filter above); null until the
   *  backend sends `totals`, which hides the Totals card. */
  totals: AdminCallTotals | null = null;
  sources: SourceSlice[] = [];
  sourcesLabel = '';
  pagination: AdminPagination | null = null;
  isLoading = false;
  /** Until the first response: the Totals card shows a skeleton meanwhile. */
  hasLoadedOnce = false;

  q = '';
  endpoint = '';
  source = '';
  apiKeyId = '';
  errorsOnly = false;
  from = '';
  to = '';
  page = 1;

  /** Phones get a Filters button and sheet instead of the inline filters. */
  readonly phone = isPhoneViewport();
  private readonly modals = inject(ModalService);

  private readonly filterFields: AdminFilterField[] = [
    { kind: 'text', key: 'endpoint', label: 'Endpoint', placeholder: 'e.g. rag' },
    { kind: 'segments', key: 'source', label: 'Source', options: [{ value: '', label: 'All' }, { value: 'api', label: 'API' }, { value: 'playground', label: 'Playground' }] },
    { kind: 'segments', key: 'status', label: 'Status', options: [{ value: 'all', label: 'All statuses' }, { value: 'errors', label: 'Errors only' }] },
    { kind: 'dates', fromKey: 'from', toKey: 'to', label: 'Date' },
  ];
  private readonly filterDefaults = { endpoint: '', source: '', status: 'all', from: '', to: '' };

  private get filterValues(): Record<string, string> {
    return { endpoint: this.endpoint, source: this.source, status: this.errorsOnly ? 'errors' : 'all', from: this.from, to: this.to };
  }

  /** Phones: every filter but the search, in a sheet; applied in one go. */
  openFilters(): void {
    openAdminFilters(this.modals, { fields: this.filterFields, values: this.filterValues, defaults: this.filterDefaults }, v => {
      this.endpoint = v['endpoint'];
      this.source = v['source'];
      this.errorsOnly = v['status'] === 'errors';
      this.from = v['from'];
      this.to = v['to'];
      this.applyFilters();
    });
  }

  /** Badge on the phone Filters button: how many filters are set. */
  get activeFilterCount(): number {
    return countActiveFilters(this.filterValues, this.filterDefaults);
  }

  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.q = params.get('q') ?? '';
    this.endpoint = params.get('endpoint') ?? '';
    this.source = params.get('source') ?? '';
    this.apiKeyId = params.get('api_key_id') ?? '';
    this.errorsOnly = params.get('errors_only') === 'true';
    this.from = params.get('from') ?? '';
    this.to = params.get('to') ?? '';
    this.page = Number(params.get('page')) || 1;

    this.reload$.pipe(
      debounceTime(250),
      switchMap(() => {
        this.isLoading = true;
        return this.adminService.getCalls({
          q: this.q.trim() || undefined,
          endpoint: this.endpoint.trim() || undefined,
          source: this.source || undefined,
          apiKeyId: this.apiKeyId || undefined,
          errorsOnly: this.errorsOnly || undefined,
          from: this.from ? `${this.from}T00:00:00Z` : undefined,
          to: this.to ? `${this.to}T23:59:59Z` : undefined,
          page: this.page,
          perPage: PER_PAGE,
        });
      }),
      takeUntil(this._destroy$),
    ).subscribe(({ rows, totals, pagination }) => {
      this.rows = rows;
      this.totals = totals;
      this.sources = sourceSlices(totals?.bySource);
      this.sourcesLabel = sourceSlicesLabel(this.sources);
      this.pagination = pagination;
      this.isLoading = false;
      this.hasLoadedOnce = true;
    });

    this.reload$.next();
  }

  get totalsFilterLabel(): string | null {
    return filterLabel([
      this.q.trim() && `“${this.q.trim()}”`,
      this.endpoint.trim() && `/${this.endpoint.trim().replace(/^\//, '')}`,
      this.source === 'api' ? 'API' : this.source === 'playground' ? 'Playground' : '',
      this.apiKeyId && 'one API key',
      this.errorsOnly && 'errors only',
      dateRangeLabel(this.from, this.to),
    ]);
  }

  /** Matches the threshold the `latency` pipe switches to seconds at. */
  isSlow(row: AdminCallRow): boolean {
    return row.latencyMs >= LatencyPipe.SLOW_MS;
  }

  private applyFilters(): void {
    this.page = 1;
    this.syncUrl();
  }

  onQueryChange(value: string): void {
    this.q = value;
    this.applyFilters();
  }

  onEndpointChange(value: string): void {
    this.endpoint = value;
    this.applyFilters();
  }

  onSourceChange(value: string): void {
    this.source = value;
    this.applyFilters();
  }

  onErrorsOnlyChange(value: string): void {
    this.errorsOnly = value === 'errors';
    this.applyFilters();
  }

  onFromChange(value: string): void {
    this.from = value;
    this.applyFilters();
  }

  onToChange(value: string): void {
    this.to = value;
    this.applyFilters();
  }

  /** Only reachable via a cross-link (Organization Detail's key rows), not
   *  a filter bar field typing a UUID isn't something anyone does by hand. */
  clearApiKeyFilter(): void {
    this.apiKeyId = '';
    this.applyFilters();
  }

  @ViewChild('listCard') private listCard?: ElementRef<HTMLElement>;
  private readonly scroller = inject(AppViewportScroller);

  goToPage(page: number): void {
    if (page < 1 || (this.pagination && page > this.pagination.pages)) return;
    this.page = page;
    // Next/Previous land on the top of the list, not the top of the page.
    this.scroller.overrideNextScroll(() => this.listCard?.nativeElement.scrollIntoView({ block: 'start' }));
    this.syncUrl();
  }

  clearFilters(): void {
    this.q = '';
    this.endpoint = '';
    this.source = '';
    this.apiKeyId = '';
    this.errorsOnly = false;
    this.from = '';
    this.to = '';
    this.applyFilters();
  }

  get hasFilters(): boolean {
    return !!(this.q || this.endpoint || this.source || this.apiKeyId || this.errorsOnly || this.from || this.to);
  }

  private syncUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: this.q.trim() || null,
        endpoint: this.endpoint.trim() || null,
        source: this.source || null,
        api_key_id: this.apiKeyId || null,
        errors_only: this.errorsOnly ? 'true' : null,
        from: this.from || null,
        to: this.to || null,
        page: this.page > 1 ? this.page : null,
      },
      replaceUrl: true,
    });
    this.reload$.next();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
